import React, { useEffect, useRef, useCallback } from 'react';
import { ROLE_DEFINITIONS } from './CockpitDashboardBackground';

interface CockpitCubeTransitionProps {
  currentRoleKey: string;
  nextRoleKey: string;
  theme: 'dark' | 'light' | 'purple';
  onComplete: () => void;
}

// ───── Funções de easing (idênticas ao template WebGL) ─────
const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
const easeInOutSine = (x: number) => -(Math.cos(Math.PI * x) - 1) / 2;

export const CockpitCubeTransition: React.FC<CockpitCubeTransitionProps> = ({
  currentRoleKey,
  nextRoleKey,
  theme,
  onComplete,
}) => {
  const cubeRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const completedRef = useRef(false);

  const currentRole = ROLE_DEFINITIONS[currentRoleKey.toLowerCase() as keyof typeof ROLE_DEFINITIONS] || ROLE_DEFINITIONS.administrador;
  const nextRole = ROLE_DEFINITIONS[nextRoleKey.toLowerCase() as keyof typeof ROLE_DEFINITIONS] || ROLE_DEFINITIONS.administrador;

  const extractHex = (bgClass: string) => {
    return bgClass.match(/#([0-9a-fA-F]{3,6})/)?.[0] || '#0b0928';
  };

  const getGradient = (role: typeof currentRole) => {
    const from = extractHex(role.backgrounds[0]);
    const via = extractHex(role.backgrounds[1]);
    const to = extractHex(role.backgrounds[2]);
    return `radial-gradient(circle at 70% 30%, ${role.primary}30, transparent 50%), linear-gradient(135deg, ${from} 0%, ${via} 50%, ${to} 100%)`;
  };

  // ───── Loop de animação com requestAnimationFrame ─────
  //
  // Replica EXATAMENTE a matemática do template WebGL original:
  //   camera.position.z = 14.2 + 8.15 * spatialArc  → zoom (convertido para CSS scale)
  //   camera.position.x = sin(π × progress) * 0.82  → arco lateral (translateX)
  //   cube.rotation.y = -smoothRotation * π/2        → rotação do cubo
  //
  // O cubo é um CSS 3D real (preserve-3d) com translateZ(50vw).
  // As faces se conectam FISICAMENTE nas arestas — sem gap possível.

  const DURATION = 1800; // ms

  const animate = useCallback((now: number) => {
    if (!startTimeRef.current) startTimeRef.current = now;

    const elapsed = now - startTimeRef.current;
    const progress = clamp(elapsed / DURATION, 0, 1);

    // ─── Curvas IDÊNTICAS ao template ───
    const easedProgress = easeInOutSine(progress);
    const spatialArc = Math.sin(Math.PI * easedProgress);

    const rotationProgress = clamp((progress - 0.12) / 0.76, 0, 1);
    const smoothRotation = easeInOutSine(rotationProgress);

    // Camera Z → CSS scale
    // Original: camera.z = 14.2 + 8.15 * spatialArc (range: 14.2 → 22.35)
    // Ampliado: 14.0 para distanciar mais (range: 14.2 → 28.2)
    const scale = 14.2 / (14.2 + 14.0 * spatialArc);

    // Camera X → translateX (arco lateral suave)
    // Original: 0.82 units em um mundo de ~12 units de largura
    // Proporção: 0.82/12 ≈ 6.8% → convertido para vw
    const translateX = Math.sin(progress * Math.PI) * 3.5;

    // Camera Y → translateY (arco vertical sutil)
    const translateY = Math.sin(progress * Math.PI) * -1.0;

    // Rotação Y do cubo em graus (0 → -90°)
    const rotY = -smoothRotation * 90;

    // Aplicar transform ao cubo via ref
    if (cubeRef.current) {
      cubeRef.current.style.transform =
        `translateZ(-50vw) translateX(${translateX}vw) translateY(${translateY}vh) rotateY(${rotY}deg) scale(${scale})`;
    }

    if (progress < 1) {
      animFrameRef.current = requestAnimationFrame(animate);
    } else {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete();
      }
    }
  }, [onComplete]);

  useEffect(() => {
    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [animate]);

  const renderFace = (role: typeof currentRole, isCurrent: boolean) => (
    <>
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center border border-white/20 shadow-lg text-white [&_svg]:w-7 [&_svg]:h-7"
          style={{ background: `linear-gradient(135deg, ${role.primary}, ${role.secondary})` }}
        >
          {role.icon}
        </div>
        <div className="flex flex-col">
          <strong className="text-xs font-bold text-white tracking-wide">Aluno Conectado</strong>
          <span className="text-[8px] font-extrabold text-white/50 uppercase tracking-widest mt-0.5">{role.environment}</span>
        </div>
      </div>

      <div className="max-w-xl pl-6">
        <h2
          className="text-4xl md:text-5xl font-black text-white leading-none tracking-tight mb-4"
          dangerouslySetInnerHTML={{ __html: role.title }}
        />
        <p className="text-[11px] leading-relaxed text-white/60 max-w-md">{role.description}</p>
      </div>

      {isCurrent ? (
        <div className="h-2 w-full max-w-sm bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full bg-white/40 w-1/2"
            style={{ animation: 'sweepGlow 1.2s ease forwards' }}
          />
        </div>
      ) : (
        <div className="text-[9px] font-bold text-[#a6e3a1] flex items-center gap-1.5 uppercase tracking-widest">
          <span className="inline-block w-1.5 h-1.5 bg-[#a6e3a1] rounded-full animate-pulse" />
          Sintonizando ambiente seguro...
        </div>
      )}
    </>
  );

  // Cor do material escuro das arestas (igual ao template: 0x07101d)
  const darkEdge = '#07101d';

  return (
    <>
      <style>{`
        @keyframes sweepGlow {
          0% { transform: translateX(-100%) skewX(-20deg); }
          100% { transform: translateX(100%) skewX(-20deg); }
        }
      `}</style>

      {/* ═══ Scene (Perspective Container) ═══ */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          // Perspectiva calibrada ao template: camera FOV 42° a distância 14.2
          // perspective ≈ viewportHeight / (2 * tan(FOV/2))
          // Com FOV 42°: perspective ≈ 100vh / (2 * tan(21°)) ≈ 130vh
          perspective: '130vh',
          perspectiveOrigin: '50% 50%',
          backgroundColor: '#04020a',
          overflow: 'hidden',
          userSelect: 'none',
        }}
      >
        {/* ═══ Cube (preserve-3d — faces conectam nas arestas) ═══ */}
        <div
          ref={cubeRef}
          style={{
            position: 'absolute',
            inset: 0,
            transformStyle: 'preserve-3d',
            willChange: 'transform',
            // Estado inicial: face frontal visível
            transform: 'translateZ(-50vw) rotateY(0deg) scale(1)',
          }}
        >
          {/* ─── Front Face (face Z+) = Ambiente Atual ─── */}
          <div
            className="flex flex-col justify-between p-8 pb-20 text-left"
            style={{
              position: 'absolute',
              inset: 0,
              backfaceVisibility: 'hidden',
              transform: 'translateZ(50vw)',
              background: getGradient(currentRole),
            }}
          >
            {renderFace(currentRole, true)}
            <div
              style={{
                position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04,
                backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 69px, rgba(255,255,255,0.4) 70px), repeating-linear-gradient(90deg, transparent, transparent 69px, rgba(255,255,255,0.4) 70px)',
              }}
            />
          </div>

          {/* ─── Right Face (face X+) = Novo Ambiente ─── */}
          <div
            className="flex flex-col justify-between p-8 pb-20 text-left"
            style={{
              position: 'absolute',
              inset: 0,
              backfaceVisibility: 'hidden',
              transform: 'rotateY(90deg) translateZ(50vw)',
              background: getGradient(nextRole),
            }}
          >
            {renderFace(nextRole, false)}
            <div
              style={{
                position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04,
                backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 69px, rgba(255,255,255,0.4) 70px), repeating-linear-gradient(90deg, transparent, transparent 69px, rgba(255,255,255,0.4) 70px)',
              }}
            />
          </div>

          {/* ─── Top Edge (material escuro como o template) ─── */}
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100vw',
              left: 0,
              top: '50%',
              transform: 'rotateX(90deg) translateZ(50vh)',
              transformOrigin: 'center center',
              background: darkEdge,
              backfaceVisibility: 'hidden',
            }}
          />

          {/* ─── Bottom Edge ─── */}
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100vw',
              left: 0,
              top: '50%',
              transform: 'rotateX(-90deg) translateZ(50vh)',
              transformOrigin: 'center center',
              background: darkEdge,
              backfaceVisibility: 'hidden',
            }}
          />
        </div>

        {/* ═══ Legenda ═══ */}
        <div
          style={{
            position: 'absolute',
            bottom: '2.5rem',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.25rem',
            zIndex: 10,
          }}
        >
          <span className="text-[9px] font-extrabold uppercase tracking-widest text-white/60">
            Transição de Ambiente
          </span>
          <span className="text-[8px] font-extrabold uppercase tracking-wider text-white/40">
            Cubo 3D Imersivo
          </span>
        </div>
      </div>
    </>
  );
};
